import type { ComponentChildren } from 'preact';
import { useId, useState } from 'preact/hooks';
import { ChevronDown } from 'lucide-preact';
import { Icon } from '../primitives/Icon';
import { cn } from '@/lib/cn';

export interface AccordionItem {
  id: string;
  title: ComponentChildren;
  description?: ComponentChildren;
  content: ComponentChildren;
  defaultOpen?: boolean;
  disabled?: boolean;
}

export interface AccordionProps {
  items: AccordionItem[];
  multiple?: boolean;
  className?: string;
}

export function Accordion({ items, multiple = false, className }: AccordionProps) {
  const baseId = useId();
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(items.filter((it) => it.defaultOpen).map((it) => it.id)),
  );

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(multiple ? prev : []);
      if (prev.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div className={cn('flex flex-col divide-y divide-line-subtle rounded-lg border border-border-subtle bg-surface', className)}>
      {items.map((it) => {
        const isOpen = open.has(it.id);
        const headerId = `${baseId}-h-${it.id}`;
        const panelId = `${baseId}-p-${it.id}`;
        return (
          <div key={it.id}>
            <h3>
              <button
                type="button"
                id={headerId}
                aria-controls={panelId}
                aria-expanded={isOpen}
                disabled={it.disabled}
                onClick={() => !it.disabled && toggle(it.id)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-13 font-medium text-fg-strong transition-colors duration-med hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer outline-none focus-visible:shadow-focus',
                )}
              >
                <span className="flex flex-col gap-0.5">
                  <span>{it.title}</span>
                  {it.description ? (
                    <span className="text-xs font-normal text-fg-muted">{it.description}</span>
                  ) : null}
                </span>
                <Icon
                  as={ChevronDown}
                  size={16}
                  className={cn('text-fg-muted transition-transform duration-med', isOpen && 'rotate-180')}
                />
              </button>
            </h3>
            {isOpen ? (
              <div id={panelId} role="region" aria-labelledby={headerId} className="px-4 pb-4 pt-1 text-13 text-fg">
                {it.content}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
